// Explorer — one search box over people and projects.
//
// This is the console's single browse surface. It absorbed two pages that were
// duplicates of it: User Administration (a second users table) and Customer
// Support (a second people/projects list plus its own person + conversation
// views). What survives of both is their DATA, not their screens:
//
//   * User Administration's Block/Delete buttons live on the people rows and on
//     a person's page, shown only to holders of `user_administration`.
//   * Customer Support's grant now opens THIS page in support mode, reading the
//     narrow talent-free payload. See lib/explorer-access.ts.
//
// Every table filters off the one search box and sorts client-side, because each
// list is already fully in memory: the legacy routes return the whole set, so a
// keystroke costs nothing and no request is made. If a list outgrows that, the
// fix is a server-side `q` (Customer Support's projects route already has one),
// not a debounce bolted on here.
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  Input,
  isDateRangeActive,
  isInDateRange,
  matchesQuery,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  type MultiSelectOption,
  useColumnFilters,
  usePagedRows,
  useRestoredState,
  useTableSort,
} from "@recruiter/shared";
import type { ProjectExport } from "@recruiter/shared/types";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import { AlertCell, alertSortValue } from "../components/alert-cell";
import { AreaBoundary } from "../components/area-gate";
import { ArrowRightIcon } from "../components/icons";
import { HandlingChips } from "../components/handling-chips";
import { ExperimentFilter } from "../components/experiment-filter";
import type { ExperimentScope } from "../lib/experiment-scope";
import { ProjectStageBadge } from "../components/project-stage-badge";
import { StarToggle } from "../components/star-toggle";
import { SellerIntentTag } from "../components/seller-intent-tag";
import { WorkShapeTag } from "../components/work-shape-tag";
import { ModelTierTag } from "../components/model-tier-tag";
import { EmptyState, TableSkeleton } from "../components/states";
import {
  BlockedBadge,
  UserAdminActions,
  useCanAdministerUsers,
} from "../components/user-admin-actions";
import {
  type AdminUserRow,
  ApiError,
  type ExplorerProjectFacets,
  type ExplorerProjectRow,
  importProject,
} from "../lib/api";
import { EXPLORER_VIEWS, type ExplorerView, canAct, useExplorerMode } from "../lib/explorer-access";
import {
  budgetSortValue,
  formatBudget,
  formatDate,
  formatDateTime,
  formatTimeline,
  type ProjectBudget,
  type ProjectTimeline,
} from "../lib/format";
import {
  HANDLING_KEYS,
  type HandlingSource,
  handlingFilterValue,
  handlingLabel,
} from "../lib/project-handling";
import {
  PERSON_SERVER_SORTS,
  PROJECT_SERVER_SORTS,
  alertSelectionToWire,
  noValueAs,
  selectionAsBoolean,
} from "../lib/explorer-paging";
import { PROJECT_STAGES, stageDisplay, stageSortValue } from "../lib/project-stage";
import { useDebounced } from "../lib/use-debounced";
import { isDerivedTitle, projectTitle } from "../lib/project-title";
import { DEVICE_FILTER_KEYS, deviceDetail, deviceLabel } from "../lib/device";
import { MODEL_TIER_FILTER_KEYS, modelTierLabel } from "../lib/model-tier";
import { UNKNOWN_COUNTRY, countryLabel } from "../lib/country";
import { useCountryFilterOptions } from "../components/country-filter";
import { BUYER_TYPES, buyerTypeLabel } from "../lib/buyer-type";
import { WORK_SHAPES, workShapeLabel } from "../lib/work-shape";
import {
  MEETING_VERIFICATION_LABEL,
  MEETING_VERIFICATION_TIERS,
  MEETING_VERIFICATION_TONE,
  NO_MEETING_LABEL,
} from "../lib/meeting-verification";
import {
  adminKeys,
  explorerProjectsQuery,
  explorerUsersQuery,
  supportPeopleQuery,
  supportProjectsQuery,
} from "../lib/queries";
import { ShowMoreRow } from "../components/show-more-row";
import "./explorer.css";

/** One person, in whichever shape the viewer's mode fetched. Both sources carry
 *  these fields; only full mode has `createdAt` and the admin actions. */
interface PersonRow {
  id: string;
  email: string;
  displayName: string | null;
  projectCount: number;
  lastActiveAt: string | null;
  createdAt: string | null;
  blocked: boolean;
  isGuest: boolean;
  /** The regional model tier the account was first seen on (luna / terra,
   *  migration 0195) and the country it was decided from. Null = not stamped
   *  yet, which is also what the narrower CS payload reports. */
  modelTier: string | null;
  originCountry: string | null;
}

/** One project, in whichever shape the viewer's mode fetched. */
/** The creation-device cell: the class the column prints, with the OS and browser
 *  under it. Each half is independently null — the class is what was recorded, the
 *  detail is what we could name beneath it. */
interface ProjectDevice {
  class: string | null;
  os: string | null;
  browser: string | null;
}

interface ProjectRow {
  id: string;
  name: string;
  summary: string | null;
  kind: string | null;
  /** The engagement SHAPE the agents latched (project / ongoing / hybrid) —
   *  the work-shape tag + filter. Null = not classified yet, which is also what
   *  the narrower CS payload reports (it does not carry the field). */
  workShape: string | null;
  /** WHO is paying (smb / solo_professional / individual_consumer / enterprise /
   *  unclear) — the buyer-type tag + filter. Resolved from Fiverr's own
   *  `company_size` where the account carries one, from the conversation read
   *  otherwise. Null = nothing has classified it yet, which the filter offers as
   *  its own bucket so those rows stay findable. */
  buyerType: string | null;
  /** How far the project got. Both modes carry it — the full listing and the CS
   *  one share one SQL fragment (`repo.project_stage`), so a project reads the
   *  same rung in either console. */
  stage: string | null;
  /** What the project was CREATED on (migration 0183). NULL means the payload does
   *  not carry the fields at all (support mode, or a backend older than 0183) and
   *  the table hides the column; an object with a null `class` means carried but
   *  never recorded, which the cell prints as "Unknown" — an import, an MCP turn,
   *  or a project older than the migration. */
  device: ProjectDevice | null;
  /** The regional model tier the project was CREATED on (luna / terra, migration
   *  0195) and the browser-detected country it was decided from. Null = a row
   *  older than the migration, or a payload that does not carry it (support
   *  mode); the column hides itself when no loaded row carries one. */
  modelTier: string | null;
  originCountry: string | null;
  /** What the client committed to spend and by when. NULL means the payload does
   *  not carry them at all (support mode), which is different from "carried but
   *  uncaptured" — the table hides the columns for the first and prints "-" for
   *  the second. */
  budget: ProjectBudget | null;
  timeline: ProjectTimeline | null;
  ownerLabel: string | null;
  /** The CLIENT's messages only, not the transcript length. */
  messageCount: number;
  updatedAt: string | null;
  alertCount: number;
  alertReasons: string[];
  /** Talent pitching their services, not a client buying them. A durable
   *  tag, so it stays after the matching alert is resolved. */
  sellerIntent: boolean;
  /** Who is on this project RIGHT NOW — takeover, pause, a search in flight.
   *  Beside the stage badge rather than on the ladder, because the ladder is
   *  cumulative and these are reversible (`lib/project-handling.ts`). Absent in
   *  support mode, whose narrower payload does not carry them; the chips then
   *  simply never render. */
  handling: HandlingSource;
  /** Starred by the CALLING operator (migration 0158), so a row can be kept in
   *  view. Absent in support mode, which has no star control. */
  starred: boolean | null;
  /** The desk + verification tier of this project's MOST RECENTLY claimed
   *  specialist-call meeting (`meetings` table, migration 0182). Both null when
   *  never claimed, or when the payload does not carry them (support mode). A
   *  project can hold several claims across tiers; open the Meetings tab on its
   *  own page for the whole history. */
  handoffTrack: string | null;
  meetingVerification: string | null;
}

function personLabel(person: PersonRow): string {
  if (person.isGuest) return `Guest · ${person.id.replace(/^guest_/, "").slice(0, 8)}`;
  return person.displayName || person.email;
}

const PERSON_SORT = {
  person: (p: PersonRow) => personLabel(p),
  projects: (p: PersonRow) => p.projectCount,
  lastActive: (p: PersonRow) => p.lastActiveAt,
  joined: (p: PersonRow) => p.createdAt,
  status: (p: PersonRow) => p.blocked,
  modelTier: (p: PersonRow) => p.modelTier ?? "",
} as const;

/** Column labels for the "sorted over loaded rows only" note. Spelled out rather
 *  than derived from the header cells: the note is a sentence, and "lastActive"
 *  is not a word. */
const PERSON_SORT_LABELS: Record<string, string> = {
  person: "person",
  projects: "project count",
  lastActive: "last active",
  joined: "joined",
  status: "status",
  modelTier: "model",
};

/** Column labels for the projects table's "loaded rows only" note. */
const PROJECT_SORT_LABELS: Record<string, string> = {
  project: "project",
  stage: "stage",
  kind: "kind",
  workShape: "work shape",
  buyerType: "buyer type",
  device: "device",
  modelTier: "model",
  budget: "budget",
  timeline: "timeline",
  owner: "owner",
  alerts: "alerts",
  meeting: "meeting",
  messages: "messages",
  updated: "last updated",
};

/** The sort keys each listing can push to the server, as the `serverSortable`
 *  list its table takes. */
const PERSON_SERVER_SORTABLE = Object.keys(PERSON_SERVER_SORTS);
const PROJECT_SERVER_SORTABLE = Object.keys(PROJECT_SERVER_SORTS);

const PROJECT_SORT = {
  // Sort on what the row DISPLAYS. Sorting on the raw `name` would scatter every
  // blank-named project to one end regardless of the title actually shown.
  project: (p: ProjectRow) => projectTitle(p),
  // Ladder position, not the label: sorting the words alphabetically would put
  // "brief" above "search" and make the column useless for reading the funnel.
  stage: (p: ProjectRow) => stageSortValue(p.stage),
  kind: (p: ProjectRow) => p.kind,
  // Sorted on the CLASS, with an unrecorded device sorting as the empty string so
  // the Unknown block stays together rather than scattering through the list.
  device: (p: ProjectRow) => p.device?.class ?? "",
  // Same rule for the model lane: an unstamped row sorts as the empty string so
  // the Unknown block stays together.
  modelTier: (p: ProjectRow) => p.modelTier ?? "",
  // Sorted on the committed total, or one month of a committed rate — the same
  // effective figure the big-client alerts judge — so descending finds the big
  // clients whatever shape their money takes. An uncaptured budget is null,
  // which the table sorts last in BOTH directions — a blank is unknown, not
  // "cheapest".
  budget: (p: ProjectRow) => budgetSortValue(p.budget),
  timeline: (p: ProjectRow) => p.timeline?.deliveryMaxDays ?? null,
  owner: (p: ProjectRow) => p.ownerLabel,
  // Urgency-weighted, so descending puts what needs a human first.
  alerts: (p: ProjectRow) => alertSortValue(p.alertCount, p.alertReasons),
  // Sorted on the CLASS, same rule as device/modelTier: an unclaimed row sorts
  // as the empty string so the "no meeting" block stays together.
  meeting: (p: ProjectRow) => p.meetingVerification ?? "",
  messages: (p: ProjectRow) => p.messageCount,
  updated: (p: ProjectRow) => p.updatedAt,
} as const;

/** Urgency-first, matching the tones the alert chips are badged with and the
 *  order the Alerts worklist offers. Shared by the two project tables. */
const ALERT_REASON_ORDER = [
  "agent_error",
  "chat_misuse",
  "human_requested",
  "client_waiting",
  "frustration",
] as const;

/** A flag with no reason recorded. Parenthesized so it cannot collide with a
 *  snake_case reason off the wire, and named rather than folded into "No
 *  alerts": a project IS flagged, and putting it in the clear bucket would be
 *  the filter telling an operator something untrue. */
const REASON_UNRECORDED = "(reason not recorded)";

const PROJECT_KIND_FILTER: FilterColumn<ProjectRow> = {
  key: "kind",
  label: "Filter by kind",
  allLabel: "All kinds",
  // The cell prints "project" for a null kind, so the filter says the same.
  value: (p) => p.kind ?? "project",
};

/** The engagement shape beside the kind: one-off / ongoing / hybrid, with the
 *  not-yet-classified rows as the no-value bucket so they stay findable too. */
const PROJECT_WORK_SHAPE_FILTER: FilterColumn<ProjectRow> = {
  key: "workShape",
  label: "Filter by work shape",
  allLabel: "All work shapes",
  value: (p) => p.workShape,
  optionLabel: (value) => workShapeLabel(value),
  noValueLabel: workShapeLabel("none"),
  order: WORK_SHAPES,
};

/** WHO is paying, beside the work shape: the SMB cut an operator works the list by
 *  ("show me the SMB conversations"), with the never-classified rows as the no-value
 *  bucket so they stay findable too. */
const PROJECT_BUYER_TYPE_FILTER: FilterColumn<ProjectRow> = {
  key: "buyerType",
  label: "Filter by buyer type",
  allLabel: "All buyer types",
  value: (p) => p.buyerType,
  optionLabel: (value) => buyerTypeLabel(value),
  noValueLabel: buyerTypeLabel("none"),
  order: BUYER_TYPES,
};

/** Filters on who is on the project right now. Three states that repeat across
 *  the whole table and that an operator works the list by ("show me the ones a
 *  human already has", "show me what is still running"), which is precisely
 *  what a multi-select is for and what sorting the Stage column cannot answer.
 *  A row can carry several at once, so ticking any one keeps it. */
const PROJECT_HANDLING_FILTER: FilterColumn<ProjectRow> = {
  key: "handling",
  label: "Filter by handling",
  allLabel: "Anyone",
  value: (p) => handlingFilterValue(p.handling),
  optionLabel: handlingLabel,
  // "Nobody is on this one" is an answer an operator actively looks for, not an
  // absence to hide.
  noValueLabel: "Mira only",
  order: HANDLING_KEYS,
};

/** Filters on the device a project was CREATED on. Its `unknown` bucket is the
 *  one an operator has to be able to ask for: the column is NULL for an import,
 *  an MCP turn, and for every project older than migration 0183, which is most
 *  of the table until that migration has been shipped a while. Hiding those rows
 *  behind an unticked option would make "mobile + desktop + tablet" look like the
 *  whole population when it is a slice of it. */
const PROJECT_DEVICE_FILTER: FilterColumn<ProjectRow> = {
  key: "device",
  label: "Filter by device",
  allLabel: "All devices",
  value: (p) => p.device?.class ?? null,
  optionLabel: (value) => deviceLabel(value),
  noValueLabel: deviceLabel("unknown"),
  order: DEVICE_FILTER_KEYS,
};

/** Filters on the regional model tier a project was CREATED on (luna / terra,
 *  migration 0195). `unknown` is the rows older than the migration, offered for
 *  the same reason the device filter offers it. Shared by the projects AND the
 *  people tables (an account carries the tier it was first seen on), so the two
 *  controls cannot drift. */
const MODEL_TIER_FILTER = {
  label: "Filter by model",
  allLabel: "All models",
  optionLabel: (value: string) => modelTierLabel(value),
  noValueLabel: modelTierLabel("unknown"),
  order: MODEL_TIER_FILTER_KEYS,
  /** Closed on both sides, so the whole vocabulary is offered whether or not the
   *  loaded page happens to contain a Luna row. */
  options: MODEL_TIER_FILTER_KEYS.filter((key) => key !== "unknown").map((key) => ({
    value: key,
    label: modelTierLabel(key),
  })),
} as const;

/** Filters on the origin COUNTRY (migration 0195) a project was CREATED from, or an
 *  account first seen from. Shared by the projects AND the people tables. The
 *  vocabulary is OPEN (any code a browser reports), so in server mode the options
 *  come from the store's facet (`useCountryFilterOptions`) rather than a constant;
 *  `unknown` is the rows older than the migration and every import and MCP turn,
 *  offered for the device filter's reason. */
const COUNTRY_FILTER = {
  label: "Filter by country",
  allLabel: "All countries",
  optionLabel: (value: string) => countryLabel(value),
  noValueLabel: countryLabel(UNKNOWN_COUNTRY),
} as const;

const PROJECT_MODEL_TIER_FILTER: FilterColumn<ProjectRow> = {
  key: "modelTier",
  label: MODEL_TIER_FILTER.label,
  allLabel: MODEL_TIER_FILTER.allLabel,
  value: (p) => p.modelTier,
  optionLabel: MODEL_TIER_FILTER.optionLabel,
  noValueLabel: MODEL_TIER_FILTER.noValueLabel,
  order: MODEL_TIER_FILTER.order,
};

/** Filters on the REASONS, not on how many: a project with one `agent_error` and
 *  a project with one `frustration` are different jobs, and "3 alerts" is not a
 *  thing anybody triages by. A row carries several reasons at once, so ticking
 *  either one keeps it. */
const PROJECT_ALERT_FILTER: FilterColumn<ProjectRow> = {
  key: "alerts",
  label: "Filter by alert",
  allLabel: "Any alerts",
  value: (p) =>
    p.alertCount === 0 ? null : p.alertReasons.length > 0 ? p.alertReasons : [REASON_UNRECORDED],
  optionLabel: (value) => value.replaceAll("_", " "),
  noValueLabel: "No alerts",
  order: ALERT_REASON_ORDER,
};

/** Filters on the verification TIER of the project's most recently claimed
 *  specialist-call meeting — not on booked/not-booked, which is the old single
 *  bit the Big-clients lane's cell still reads. A row carries at most one tier
 *  here (its latest claim); a project that never claimed one files under the
 *  no-value bucket. */
const PROJECT_MEETING_FILTER: FilterColumn<ProjectRow> = {
  key: "meeting",
  label: "Filter by meeting",
  allLabel: "Any meeting state",
  value: (p) => p.meetingVerification,
  optionLabel: (value) => MEETING_VERIFICATION_LABEL[value] ?? value,
  noValueLabel: NO_MEETING_LABEL,
  order: MEETING_VERIFICATION_TIERS,
};

/** What a table needs when its rows are narrowed, ordered and paged by the
 *  SERVER rather than held whole in the browser.
 *
 *  The Explorer's two big listings are keyset-paged (200 a page) since the
 *  unbounded reads behind them started timing out in prod. That moves three
 *  jobs across the wire, and each of them is a correctness fix rather than a
 *  preference:
 *
 *    * FILTERS must narrow server-side, or a selection answers "nothing
 *      matches" from the first page while the match sits on page nine;
 *    * SORT likewise, for the columns the database can order by — see
 *      `serverSortable` for the ones it cannot;
 *    * PAGING is then just "fetch the next cursor", which the shared
 *      `ShowMoreRow` drains after the local window.
 *
 *  Absent on a table whose rows arrived whole (support mode's narrow CS
 *  listing), where every one of those stays a local recompute. */
interface ServerList {
  hasMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  /** The sort keys the server can order the WHOLE list by. A key outside this
   *  set sorts only the rows loaded so far, and the table says so. */
  serverSortable: readonly string[];
  onSortChange: (sort: { key: string; direction: "asc" | "desc" } | null) => void;
}

/** The note under a table sorted on a column the server cannot order by.
 *  Printed rather than left implicit: an operator who sorts by Stage and reads
 *  the top row as "the furthest along project there is" would be wrong, and
 *  nothing else on screen would tell them. */
function LoadedOnlySortNote({ column }: { column: string }) {
  return (
    <p className="ex-sort-note">
      Sorted by {column} across the rows loaded so far, not the whole list. Load more rows, or
      narrow with the filters, to sort a smaller set completely.
    </p>
  );
}

function PeopleTable({
  rows,
  q,
  range,
  onRangeChange,
  statusFilter,
  onStatusChange,
  accountFilter,
  onAccountChange,
  tierFilter,
  onTierChange,
  countryFilter,
  onCountryChange,
  countryOptions,
  server,
  scope,
  onScopeChange,
}: {
  rows: PersonRow[];
  q: string;
  /** Guests / signed-up, held by the caller when the SERVER applies it. */
  accountFilter?: readonly string[];
  onAccountChange?: (next: readonly string[]) => void;
  /** The regional model tier the account was first seen on, same contract. */
  tierFilter?: readonly string[];
  onTierChange?: (next: readonly string[]) => void;
  /** The origin country the account was first seen from, same contract; the
   *  options come from the store's facet because the vocabulary is open. */
  countryFilter?: readonly string[];
  onCountryChange?: (next: readonly string[]) => void;
  countryOptions?: readonly MultiSelectOption[];
  /** Present when the rows are narrowed, ordered and paged server-side. */
  server?: ServerList;
  /** When they JOINED. Held in the URL beside `?q=` and `?status=`, so a
   *  date-scoped list is a link like every other Explorer view. */
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  /** Statuses to keep, held in the URL: `?status=blocked` IS the blocked-account
   *  roster. Empty or absent shows everyone. */
  statusFilter?: readonly string[];
  /** Absent on a surface with no URL to write the filter into, which leaves the
   *  Status control working exactly the same, just not shareable. */
  onStatusChange?: (next: readonly string[]) => void;
  /** The A/B group this list is narrowed to, applied SERVER-side like `q` and
   *  the range. Absent `onScopeChange` hides the control, on the same rule as
   *  `onStatusChange`: a surface with no URL to write it into. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
}) {
  const canAdminister = useCanAdministerUsers();
  // The range is folded HERE in full mode (this list arrives whole) and has
  // already been applied by the server in support mode, which passes an empty
  // range in — the same split `q` uses two lines down. A row with no join date
  // has nothing to compare, so it drops out of a bounded range rather than being
  // counted into every one of them.
  const inRange = useMemo(
    () =>
      isDateRangeActive(range)
        ? rows.filter((p) => p.createdAt !== null && isInDateRange(p.createdAt.slice(0, 10), range))
        : rows,
    [rows, range],
  );
  const searched = useMemo(
    () => inRange.filter((p) => matchesQuery(q, p.email, p.displayName, p.id)),
    [inRange, q],
  );

  // Counts over the SEARCHED set, printed on the options themselves. The button
  // this control replaced said "Blocked only (3)" and that number was the point:
  // "nobody is blocked" is an answer somebody came here for. Now both halves
  // carry it.
  const statusOptions = useMemo(() => {
    // Counted off the loaded rows, which is only honest when they ARE the list.
    // Server-side the count would be "blocked people on this page", printed on a
    // control whose whole job is to say how many there are in total, so the
    // server-paged version drops the numbers rather than printing wrong ones.
    if (server) {
      return [
        { value: "active", label: "Active" },
        { value: "blocked", label: "Blocked" },
      ];
    }
    const blocked = searched.filter((p) => p.blocked).length;
    return [
      { value: "active", label: `Active (${(searched.length - blocked).toLocaleString()})` },
      { value: "blocked", label: `Blocked (${blocked.toLocaleString()})` },
    ];
  }, [searched, server]);

  const filterColumns = useMemo<readonly FilterColumn<PersonRow>[]>(
    () => [
      {
        key: "status",
        label: "Filter by status",
        allLabel: "Any status",
        value: (p) => (p.blocked ? "blocked" : "active"),
        // CLOSED, so both options are listed at zero rather than the control
        // quietly losing the half nobody is in. It is also the one filter here
        // held in the URL, which is what keeps the blocked roster a link the
        // launch dashboard and a Slack message can point at.
        options: statusOptions,
        selected: statusFilter ?? [],
        onChange: onStatusChange,
      },
      {
        key: "account",
        label: "Filter by account",
        allLabel: "Everyone",
        // Whether they ever signed up is a different question from whether we
        // turned Mira off for them, so it gets its own control rather than a
        // third status.
        value: (p) => (p.isGuest ? "guest" : "registered"),
        optionLabel: (value) => (value === "guest" ? "Guests" : "Signed up"),
        order: ["registered", "guest"],
        // Closed server-side, where the options cannot be derived: a page
        // narrowed to guests contains no signed-up row to derive "Signed up"
        // from, and the control would lose the option that clears it. It is a
        // vocabulary this bundle owns either way.
        options: server
          ? [
              { value: "registered", label: "Signed up" },
              { value: "guest", label: "Guests" },
            ]
          : undefined,
        selected: server ? (accountFilter ?? []) : undefined,
        onChange: server ? onAccountChange : undefined,
      },
      {
        key: "modelTier",
        label: MODEL_TIER_FILTER.label,
        allLabel: MODEL_TIER_FILTER.allLabel,
        // Which model lane the account was first seen on (migration 0195). The
        // no-value bucket is every account stamped before the lane existed.
        value: (p) => p.modelTier,
        optionLabel: MODEL_TIER_FILTER.optionLabel,
        noValueLabel: MODEL_TIER_FILTER.noValueLabel,
        order: MODEL_TIER_FILTER.order,
        options: server ? MODEL_TIER_FILTER.options : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (tierFilter ?? []) : undefined,
        onChange: server ? onTierChange : undefined,
      },
      {
        key: "originCountry",
        label: COUNTRY_FILTER.label,
        allLabel: COUNTRY_FILTER.allLabel,
        // Where the account was first seen from (migration 0195). The no-value
        // bucket is every account stamped before the lane existed.
        value: (p) => p.originCountry,
        optionLabel: COUNTRY_FILTER.optionLabel,
        noValueLabel: COUNTRY_FILTER.noValueLabel,
        options: server ? countryOptions : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (countryFilter ?? []) : undefined,
        onChange: server ? onCountryChange : undefined,
      },
    ],
    [
      statusOptions,
      statusFilter,
      onStatusChange,
      accountFilter,
      onAccountChange,
      tierFilter,
      onTierChange,
      countryFilter,
      onCountryChange,
      countryOptions,
      server,
    ],
  );

  // Restored on the way BACK from a person or project screen, so opening a record
  // off this list and returning does not cost the operator the view they built to
  // find it. Keys are unique per app; `lib/view-state-keys.test.ts` holds that.
  const filters = useColumnFilters(searched, filterColumns, {
    restore: "explorer/people/filters",
  });
  const filtered = filters.rows;
  const sorted = useTableSort(filtered, PERSON_SORT, null, { restore: "explorer/people/sort" });
  const paged = usePagedRows(sorted.rows, { restore: "explorer/people/window" });
  // Tell the server which column the reader picked, so a sort it CAN do is done
  // over the whole list rather than over the pages that happen to be loaded.
  const onSortChange = server?.onSortChange;
  useEffect(() => {
    onSortChange?.(sorted.sort);
  }, [sorted.sort, onSortChange]);
  const loadedOnlySort =
    server && sorted.sort && !server.serverSortable.includes(sorted.sort.key)
      ? sorted.sort.key
      : null;

  // Always rendered now: the date range is a control this table always offers,
  // where the column filters can legitimately come back empty (a list that is
  // all one kind has nothing to narrow by).
  const filterBar = (
    <div className="ex-list-filter">
      <span className="ex-list-filter__label">Show:</span>
      <DateRangeFilter label="Joined" value={range} onChange={onRangeChange} />
      {onScopeChange ? <ExperimentFilter scope={scope} onChange={onScopeChange} /> : null}
      {filters.controls.length > 0 ? (
        <TableFilters
          controls={filters.controls}
          active={filters.active || isDateRangeActive(range)}
          onClear={() => {
            filters.clear();
            onRangeChange(EMPTY_DATE_RANGE);
          }}
        />
      ) : null}
    </div>
  );

  if (filtered.length === 0) {
    return (
      <>
        {filterBar}
        <EmptyState
          title={
            filters.active || isDateRangeActive(range)
              ? q
                ? `No people match “${q}” and those filters`
                : "No people match those filters"
              : q
                ? `No people match “${q}”`
                : "No people yet"
          }
          message={
            filters.active
              ? "Blocking a person stops the assistant answering them until you unblock them."
              : q
                ? "Try an email, a name, or a user id."
                : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {filterBar}
      {loadedOnlySort ? <LoadedOnlySortNote column={PERSON_SORT_LABELS[loadedOnlySort]} /> : null}
      <div className="rc-table-wrap">
        <table className="rc-table">
          <TableExport name="people" label="people" />
          <thead>
            <tr>
              <SortableTh sortKey="person" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Person
              </SortableTh>
              <SortableTh
                sortKey="projects"
                numeric
                ariaSort={sorted.ariaSort}
                onToggle={sorted.toggle}
              >
                Projects
              </SortableTh>
              <SortableTh sortKey="lastActive" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Last active
              </SortableTh>
              <SortableTh sortKey="joined" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Joined
              </SortableTh>
              <SortableTh sortKey="status" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Status
              </SortableTh>
              <SortableTh sortKey="modelTier" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Model
              </SortableTh>
              {canAdminister ? <th className="rc-actions-col">Actions</th> : null}
              <th>
                <span className="ad-visually-hidden">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.shown.map((person) => (
              <tr key={person.id} data-blocked={person.blocked || undefined}>
                <td>
                  <Link
                    to="/explorer/users/$id"
                    params={{ id: person.id }}
                    className="ex-primary-link"
                  >
                    {personLabel(person)}
                  </Link>
                  {!person.isGuest && person.displayName ? (
                    <span className="rc-cell-sub">{person.email}</span>
                  ) : null}
                </td>
                <td className="rc-num">{person.projectCount.toLocaleString()}</td>
                <td>{formatDateTime(person.lastActiveAt)}</td>
                <td>{formatDate(person.createdAt)}</td>
                <td>
                  {person.blocked ? (
                    <BlockedBadge blocked />
                  ) : (
                    <Badge tone="neutral" size="sm">
                      Active
                    </Badge>
                  )}
                </td>
                <td>
                  {/* Which model lane the account was first seen on (migration 0195).
                      "Unknown" is an account stamped before the lane existed, or a
                      payload that does not carry it (support mode). */}
                  {person.modelTier ? (
                    <ModelTierTag tier={person.modelTier} country={person.originCountry} />
                  ) : (
                    <Badge tone="neutral" size="sm">
                      {modelTierLabel(null)}
                    </Badge>
                  )}
                </td>
                {canAdminister ? (
                  <td>
                    <UserAdminActions
                      user={{
                        id: person.id,
                        email: person.email,
                        blocked: person.blocked,
                      }}
                    />
                  </td>
                ) : null}
                <td>
                  <Link
                    to="/explorer/users/$id"
                    params={{ id: person.id }}
                    className="ex-row-link"
                    aria-label={`Open ${personLabel(person)}`}
                  >
                    <ArrowRightIcon size={16} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ShowMoreRow
          remaining={paged.remaining}
          onClick={paged.showMore}
          hasMore={server?.hasMore}
          onLoadMore={server?.onLoadMore}
          loading={server?.loading}
          label="people"
        />
      </div>
    </>
  );
}

function ProjectsTable({
  rows,
  q,
  range,
  onRangeChange,
  stageFilter,
  onStageChange,
  kindFilter,
  onKindChange,
  shapeFilter,
  onShapeChange,
  buyerTypeFilter,
  onBuyerTypeChange,
  handlingFilter,
  onHandlingChange,
  alertFilter,
  onAlertChange,
  deviceFilter,
  onDeviceChange,
  tierFilter,
  onTierChange,
  countryFilter,
  onCountryChange,
  countryOptions,
  meetingFilter,
  onMeetingChange,
  starredFilter,
  onStarredChange,
  facets,
  server,
  scope,
  onScopeChange,
}: {
  /** Selections the SERVER applies, held by the caller. Absent on a table whose
   *  rows arrived whole, where the same controls narrow in the browser. */
  kindFilter?: readonly string[];
  onKindChange?: (next: readonly string[]) => void;
  shapeFilter?: readonly string[];
  onShapeChange?: (next: readonly string[]) => void;
  buyerTypeFilter?: readonly string[];
  onBuyerTypeChange?: (next: readonly string[]) => void;
  handlingFilter?: readonly string[];
  onHandlingChange?: (next: readonly string[]) => void;
  alertFilter?: readonly string[];
  onAlertChange?: (next: readonly string[]) => void;
  deviceFilter?: readonly string[];
  onDeviceChange?: (next: readonly string[]) => void;
  tierFilter?: readonly string[];
  onTierChange?: (next: readonly string[]) => void;
  /** The origin country the project was CREATED from, same contract; the
   *  options come from the store's facet because the vocabulary is open. */
  countryFilter?: readonly string[];
  onCountryChange?: (next: readonly string[]) => void;
  countryOptions?: readonly MultiSelectOption[];
  /** The verification tier of the project's most recently claimed specialist-call
   *  meeting, same contract as the other closed-vocabulary filters. */
  meetingFilter?: readonly string[];
  onMeetingChange?: (next: readonly string[]) => void;
  starredFilter?: readonly string[];
  onStarredChange?: (next: readonly string[]) => void;
  /** The store-wide vocabulary for the OPEN filters, from the first page. */
  facets?: ExplorerProjectFacets | null;
  server?: ServerList;
  rows: ProjectRow[];
  q: string;
  /** When the project last MOVED — the "Updated" column, which is also what this
   *  list sorts and pages by. Held in the URL beside the other filters. */
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  /** The A/B group this list is narrowed to, applied SERVER-side like `q` and
   *  the range; absent `onScopeChange` hides the control. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
  /** Stage keys to keep, held in the URL; empty = no filter. */
  stageFilter?: readonly string[];
  /** Absent on a surface with no URL to write the filter into. */
  onStageChange?: (next: readonly string[]) => void;
}) {
  // The cross-user list carries an owner; the per-user one does not (you are
  // already looking at the owner). Hide the column rather than fill it with "-".
  const showOwner = rows.some((p) => p.ownerLabel !== null);
  // Same rule for the star: support mode carries no `starred` field and has no
  // route to write one, so hide the column there rather than draw a control
  // whose every click would 404.
  const showStar = rows.some((p) => p.starred !== null);
  // Same rule for the money columns: the support payload does not carry them at
  // all, so hide them there rather than printing a column of "-" that would read
  // as "no client has a budget".
  const showBudget = rows.some((p) => p.budget !== null);
  // Same split as the budget columns: NULL is "this payload does not carry it"
  // (support mode, or a backend older than 0183), which hides the column, while a
  // carried-but-unrecorded device prints "Unknown" in it.
  const showDevice = rows.some((p) => p.device !== null);
  // The model lane column: shown once any loaded row carries a tier. Support mode
  // carries none, and neither does a page of rows older than migration 0195 —
  // both hide the column rather than print a column of "Unknown".
  const showTier = rows.some((p) => p.modelTier !== null);
  // The Meeting column: shown once any loaded row carries a claim. Support mode
  // carries neither field at all (null on every row), which hides it there —
  // same split the budget/device/tier columns already draw.
  const showMeeting = rows.some((p) => p.meetingVerification !== null || p.handoffTrack !== null);

  // Folded HERE in full mode (this list arrives whole) and already applied by
  // the server in support mode, which passes an empty range in — the same split
  // `q` uses. A project with no `updatedAt` drops out of a bounded range: it has
  // no date to match, and counting it into every range would be worse.
  const inRange = useMemo(
    () =>
      isDateRangeActive(range)
        ? rows.filter((p) => p.updatedAt !== null && isInDateRange(p.updatedAt.slice(0, 10), range))
        : rows,
    [rows, range],
  );
  const searched = useMemo(
    () => inRange.filter((p) => matchesQuery(q, projectTitle(p), p.name, p.summary, p.id)),
    [inRange, q],
  );

  const filterColumns = useMemo<readonly FilterColumn<ProjectRow>[]>(
    () => [
      {
        key: "stage",
        label: "Filter by stage",
        allLabel: "All stages",
        // Null IS "new" everywhere else on this row (the badge prints it, the
        // sort ranks it at 0), so the filter agrees rather than inventing an
        // "Unknown" bucket for rows the table calls New.
        value: (p) => p.stage ?? "new",
        optionLabel: (value) => stageDisplay(value).label,
        order: PROJECT_STAGES,
        // Held in the URL. The Funnel's node clicks used to land here as a chip
        // an operator could only CLEAR; now they land on a control they can
        // edit, and any stage they pick stays a link they can send.
        selected: stageFilter ?? [],
        onChange: onStageChange,
        // Closed server-side: the ladder is a set both sides own, so it can be
        // listed in full whether or not the loaded page happens to use a rung.
        options: server
          ? PROJECT_STAGES.map((stage) => ({ value: stage, label: stageDisplay(stage).label }))
          : undefined,
      },
      // Server mode makes each of these CONTROLLED (the selection lives with the
      // caller, who passes it to the query) and CLOSED where the vocabulary is
      // knowable — from this bundle's own constants for the sets it owns, and from
      // the payload's `facets` for the two the backend can extend. Options derived
      // from server-filtered rows would collapse to the values already ticked,
      // leaving no control that could widen the selection again.
      {
        ...PROJECT_KIND_FILTER,
        options: server
          ? (facets?.kinds ?? []).map((kind: string) => ({ value: kind, label: kind }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (kindFilter ?? []) : undefined,
        onChange: server ? onKindChange : undefined,
      },
      {
        ...PROJECT_WORK_SHAPE_FILTER,
        options: server
          ? WORK_SHAPES.map((shape) => ({ value: shape, label: workShapeLabel(shape) }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (shapeFilter ?? []) : undefined,
        onChange: server ? onShapeChange : undefined,
      },
      {
        ...PROJECT_BUYER_TYPE_FILTER,
        options: server
          ? BUYER_TYPES.map((type) => ({ value: type, label: buyerTypeLabel(type) }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (buyerTypeFilter ?? []) : undefined,
        onChange: server ? onBuyerTypeChange : undefined,
      },
      {
        ...PROJECT_HANDLING_FILTER,
        options: server
          ? HANDLING_KEYS.map((key) => ({ value: key, label: handlingLabel(key) }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (handlingFilter ?? []) : undefined,
        onChange: server ? onHandlingChange : undefined,
      },
      {
        ...PROJECT_DEVICE_FILTER,
        // Closed on both sides, so the whole vocabulary is offered whether or not
        // the loaded page happens to contain a tablet.
        options: server
          ? DEVICE_FILTER_KEYS.filter((key) => key !== "unknown").map((key) => ({
              value: key,
              label: deviceLabel(key),
            }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (deviceFilter ?? []) : undefined,
        onChange: server ? onDeviceChange : undefined,
      },
      {
        ...PROJECT_MODEL_TIER_FILTER,
        options: server ? MODEL_TIER_FILTER.options : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (tierFilter ?? []) : undefined,
        onChange: server ? onTierChange : undefined,
      },
      {
        key: "originCountry",
        label: COUNTRY_FILTER.label,
        allLabel: COUNTRY_FILTER.allLabel,
        value: (p) => p.originCountry,
        optionLabel: COUNTRY_FILTER.optionLabel,
        noValueLabel: COUNTRY_FILTER.noValueLabel,
        options: server ? countryOptions : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (countryFilter ?? []) : undefined,
        onChange: server ? onCountryChange : undefined,
      },
      {
        ...PROJECT_ALERT_FILTER,
        options: server
          ? (facets?.alertReasons ?? []).map((reason: string) => ({
              value: reason,
              label: reason.replaceAll("_", " "),
            }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (alertFilter ?? []) : undefined,
        onChange: server ? onAlertChange : undefined,
      },
      {
        ...PROJECT_MEETING_FILTER,
        // Closed on both sides, so the whole vocabulary is offered whether or
        // not the loaded page happens to contain a confirmed one.
        options: server
          ? MEETING_VERIFICATION_TIERS.map((tier) => ({
              value: tier,
              label: MEETING_VERIFICATION_LABEL[tier] ?? tier,
            }))
          : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (meetingFilter ?? []) : undefined,
        onChange: server ? onMeetingChange : undefined,
      },
      // Starred is the operator's OWN mark, so it offers one option and the
      // shared control renders nothing on a table where nobody has starred
      // anything yet — declaring it is safe (shared `table-filter.ts`).
      {
        key: "starred",
        label: "Filter by starred",
        allLabel: "Starred or not",
        value: (p) => (p.starred ? "starred" : null),
        optionLabel: () => "Starred",
        noValueLabel: "Not starred",
        // Server-side the mark is the CALLER's own, so "starred" is a question
        // worth asking even on a page where no loaded row carries it.
        options: server ? [{ value: "starred", label: "Starred" }] : undefined,
        hasNoValue: server ? true : undefined,
        selected: server ? (starredFilter ?? []) : undefined,
        onChange: server ? onStarredChange : undefined,
      },
    ],
    [
      stageFilter,
      onStageChange,
      kindFilter,
      onKindChange,
      shapeFilter,
      onShapeChange,
      buyerTypeFilter,
      onBuyerTypeChange,
      handlingFilter,
      onHandlingChange,
      alertFilter,
      onAlertChange,
      deviceFilter,
      onDeviceChange,
      tierFilter,
      onTierChange,
      countryFilter,
      onCountryChange,
      countryOptions,
      meetingFilter,
      onMeetingChange,
      starredFilter,
      onStarredChange,
      facets,
      server,
    ],
  );

  const filters = useColumnFilters(searched, filterColumns, {
    restore: "explorer/projects/filters",
  });
  const filtered = filters.rows;
  const sorted = useTableSort(filtered, PROJECT_SORT, null, { restore: "explorer/projects/sort" });
  const paged = usePagedRows(sorted.rows, { restore: "explorer/projects/window" });
  // Same split as the people table: a column the server can order by is ordered
  // across the WHOLE list; one it cannot orders the rows loaded so far, and says so.
  const onSortChange = server?.onSortChange;
  useEffect(() => {
    onSortChange?.(sorted.sort);
  }, [sorted.sort, onSortChange]);
  const loadedOnlySort =
    server && sorted.sort && !server.serverSortable.includes(sorted.sort.key)
      ? sorted.sort.key
      : null;

  const filterBar = (
    <div className="ex-list-filter">
      <span className="ex-list-filter__label">Show:</span>
      <DateRangeFilter label="Updated" value={range} onChange={onRangeChange} />
      {onScopeChange ? <ExperimentFilter scope={scope} onChange={onScopeChange} /> : null}
      {filters.controls.length > 0 ? (
        <TableFilters
          controls={filters.controls}
          active={filters.active || isDateRangeActive(range)}
          onClear={() => {
            filters.clear();
            onRangeChange(EMPTY_DATE_RANGE);
          }}
        />
      ) : null}
    </div>
  );

  if (filtered.length === 0) {
    return (
      <>
        {filterBar}
        <EmptyState
          title={
            filters.active
              ? "No projects match those filters"
              : q
                ? `No projects match “${q}”`
                : "No projects yet"
          }
          message={filters.active ? undefined : q ? "Try a project name or id." : undefined}
        />
      </>
    );
  }

  return (
    <>
      {filterBar}
      {loadedOnlySort ? <LoadedOnlySortNote column={PROJECT_SORT_LABELS[loadedOnlySort]} /> : null}
      <div className="rc-table-wrap">
        <table className="rc-table">
          <TableExport name="projects" label="projects" />
          <thead>
            <tr>
              {showStar ? (
                <th className="ex-star-col">
                  <span className="ad-visually-hidden">Starred</span>
                </th>
              ) : null}
              <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Project
              </SortableTh>
              <SortableTh sortKey="stage" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Stage
              </SortableTh>
              <SortableTh sortKey="kind" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Kind
              </SortableTh>
              {showDevice ? (
                <SortableTh sortKey="device" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Device
                </SortableTh>
              ) : null}
              {showTier ? (
                <SortableTh sortKey="modelTier" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Model
                </SortableTh>
              ) : null}
              {showBudget ? (
                <>
                  <SortableTh
                    sortKey="budget"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Budget
                  </SortableTh>
                  <SortableTh
                    sortKey="timeline"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Timeline
                  </SortableTh>
                </>
              ) : null}
              {showOwner ? (
                <SortableTh sortKey="owner" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Owner
                </SortableTh>
              ) : null}
              <SortableTh sortKey="alerts" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Alerts
              </SortableTh>
              {showMeeting ? (
                <SortableTh sortKey="meeting" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                  Meeting
                </SortableTh>
              ) : null}
              {/* Named for what it counts. The old "Messages" header sat over a
                count of the WHOLE transcript, so an operator read Mira's own
                replies as client engagement. */}
              <SortableTh
                sortKey="messages"
                numeric
                ariaSort={sorted.ariaSort}
                onToggle={sorted.toggle}
              >
                Client msgs
              </SortableTh>
              <SortableTh sortKey="updated" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                Updated
              </SortableTh>
              <th>
                <span className="ad-visually-hidden">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.shown.map((project) => (
              <tr key={project.id}>
                {showStar ? (
                  <td className="ex-star-col">
                    <StarToggle
                      projectId={project.id}
                      starred={project.starred ?? false}
                      title={projectTitle(project)}
                    />
                  </td>
                ) : null}
                <td>
                  <span className="ex-title-line">
                    <Link
                      to="/explorer/projects/$projectId"
                      params={{ projectId: project.id }}
                      className="ex-primary-link"
                    >
                      {projectTitle(project)}
                    </Link>
                    <SellerIntentTag sellerIntent={project.sellerIntent} />
                  </span>
                  {/* Say so when the title is a stand-in, so nobody mistakes a
                    derived line for a name someone actually chose. */}
                  {isDerivedTitle(project) ? (
                    <span className="rc-cell-sub">Not named yet</span>
                  ) : project.summary ? (
                    <span className="rc-cell-sub">{project.summary}</span>
                  ) : null}
                </td>
                <td>
                  <ProjectStageBadge stage={project.stage} />
                  {/* The rung says how far it got; these say who is on it now.
                    Without them a project whose team took the conversation
                    over reads "Brief" forever (Monday 3143611422). */}
                  <HandlingChips project={project.handling} />
                </td>
                <td>
                  <span className="ex-title-line">
                    <Badge tone="neutral" size="sm">
                      {project.kind ?? "project"}
                    </Badge>
                    {/* The engagement shape rides beside the kind: what the
                      thing IS, then how the client wants to WORK on it. */}
                    <WorkShapeTag shape={project.workShape} />
                  </span>
                </td>
                {showDevice ? (
                  <td>
                    {/* What it was created ON, never where it is read now: this is
                      a creation stamp, exactly like the Created date. "Unknown" is
                      an honest gap (an import, an MCP turn, anything older than
                      migration 0183), not a rendering failure. */}
                    <Badge tone="neutral" size="sm">
                      {deviceLabel(project.device?.class ?? null)}
                    </Badge>
                    {project.device && deviceDetail(project.device.os, project.device.browser) ? (
                      <span className="rc-cell-sub">
                        {deviceDetail(project.device.os, project.device.browser)}
                      </span>
                    ) : null}
                  </td>
                ) : null}
                {showTier ? (
                  <td>
                    {/* Which model lane serves it, decided once at creation from the
                      browser-detected country (migration 0195). "Unknown" is a row
                      older than the migration, not a rendering failure. */}
                    {project.modelTier ? (
                      <ModelTierTag tier={project.modelTier} country={project.originCountry} />
                    ) : (
                      <Badge tone="neutral" size="sm">
                        {modelTierLabel(null)}
                      </Badge>
                    )}
                  </td>
                ) : null}
                {showBudget ? (
                  <>
                    {/* Committed figures only. "-" is NOT-CAPTURED, never zero:
                      "this client has not said" is the thing an operator is
                      scanning this column for. A rate prints in the client's
                      own unit ("$1,500/mo"), never as an invented total. */}
                    <td className="rc-num">{formatBudget(project.budget)}</td>
                    <td
                      className="rc-num"
                      title="The delivery window the client asked for, or the engagement's shape and start"
                    >
                      {formatTimeline(project.timeline)}
                    </td>
                  </>
                ) : null}
                {showOwner ? <td>{project.ownerLabel ?? "-"}</td> : null}
                <td>
                  <AlertCell count={project.alertCount} reasons={project.alertReasons} />
                </td>
                {showMeeting ? (
                  <td>
                    {project.meetingVerification ? (
                      <Badge
                        tone={MEETING_VERIFICATION_TONE[project.meetingVerification] ?? "neutral"}
                        size="sm"
                      >
                        {MEETING_VERIFICATION_LABEL[project.meetingVerification] ??
                          project.meetingVerification}
                      </Badge>
                    ) : (
                      <span className="ex-muted">{NO_MEETING_LABEL}</span>
                    )}
                  </td>
                ) : null}
                <td className="rc-num">{project.messageCount.toLocaleString()}</td>
                <td>{formatDateTime(project.updatedAt)}</td>
                <td>
                  <Link
                    to="/explorer/projects/$projectId"
                    params={{ projectId: project.id }}
                    className="ex-row-link"
                    aria-label={`Open ${projectTitle(project)}`}
                  >
                    <ArrowRightIcon size={16} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ShowMoreRow
          remaining={paged.remaining}
          onClick={paged.showMore}
          hasMore={server?.hasMore}
          onLoadMore={server?.onLoadMore}
          loading={server?.loading}
          label="projects"
        />
      </div>
    </>
  );
}

/** People, in full mode: the legacy users list, which carries join dates and the
 *  block flag the admin actions need. */
function FullPeople({
  q,
  range,
  onRangeChange,
  statusFilter,
  onStatusChange,
  scope,
  onScopeChange,
}: {
  q: string;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  statusFilter?: readonly string[];
  onStatusChange?: (next: readonly string[]) => void;
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
}) {
  // Keyset-paged (200 a page) since the unbounded read behind this list started
  // timing out in prod. Everything that narrows or orders it therefore travels to
  // the server: a filter applied here could only ever see the pages fetched.
  const [account, setAccount] = useRestoredState<readonly string[]>("explorer/people/account", []);
  const [tiers, setTiers] = useRestoredState<readonly string[]>("explorer/people/tiers", []);
  const [countries, setCountries] = useRestoredState<readonly string[]>(
    "explorer/people/countries",
    [],
  );
  const countryOptions = useCountryFilterOptions();
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  // Debounced: `q` travels to the server now, and every keystroke would otherwise
  // be a new query key and a new request. Same 300ms every server-searched list
  // in this console uses.
  const search = useDebounced(q.trim(), 300);
  const users = useInfiniteQuery(
    explorerUsersQuery({
      q: search,
      range,
      scope,
      blocked: selectionAsBoolean(statusFilter, "blocked", "active"),
      guests: selectionAsBoolean(account, "guest", "registered"),
      // The shared control's no-value sentinel is the route's `unknown` bucket
      // (`noValueAs`): ticking Unknown used to send the sentinel itself, a 422.
      tiers: noValueAs(tiers, "unknown"),
      countries: noValueAs(countries, "unknown"),
      sort: sort ? PERSON_SERVER_SORTS[sort.key] : undefined,
      desc: sort ? sort.direction === "desc" : undefined,
    }),
  );
  const rows = useMemo<PersonRow[]>(
    () =>
      (users.data?.pages ?? []).flatMap((page) =>
        page.users.map((u: AdminUserRow) => ({
          id: u.id,
          email: u.email,
          displayName: u.displayName,
          projectCount: u.projectCount,
          lastActiveAt: u.lastActiveAt,
          createdAt: u.createdAt,
          blocked: u.blocked,
          isGuest: u.email.startsWith("guest_"),
          modelTier: u.modelTier ?? null,
          originCountry: u.originCountry ?? null,
        })),
      ),
    [users.data],
  );

  if (users.isPending) return <TableSkeleton columns={6} />;
  if (users.isError) {
    return (
      <AreaBoundary area="explorer" error={users.error} onRetry={() => void users.refetch()} />
    );
  }
  return (
    <PeopleTable
      rows={rows}
      // Both applied by the server already, so the local passes are skipped; the
      // controls still render and still write the URL. Same split the CS lists use.
      q=""
      range={EMPTY_DATE_RANGE}
      onRangeChange={onRangeChange}
      statusFilter={statusFilter}
      onStatusChange={onStatusChange}
      accountFilter={account}
      onAccountChange={setAccount}
      tierFilter={tiers}
      onTierChange={setTiers}
      countryFilter={countries}
      onCountryChange={setCountries}
      countryOptions={countryOptions}
      server={{
        hasMore: users.hasNextPage,
        onLoadMore: () => void users.fetchNextPage(),
        loading: users.isFetchingNextPage,
        serverSortable: PERSON_SERVER_SORTABLE,
        onSortChange: setSort,
      }}
      scope={scope}
      onScopeChange={onScopeChange}
    />
  );
}

/** People, in support mode: the CS people search, which is server-side on `q`.
 *  Passing the query through means a CS rep searches every person, not just a
 *  loaded page — the behaviour their old page had. */
function SupportPeople({
  scope,
  onScopeChange,
  q,
  range,
  onRangeChange,
  statusFilter,
  onStatusChange,
}: {
  q: string;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  statusFilter?: readonly string[];
  onStatusChange?: (next: readonly string[]) => void;
  /** The A/B group this list is narrowed to, applied SERVER-side. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
}) {
  // Server-side here, like `q`: this route is capped, so a browser-side date
  // filter would narrow the returned slice rather than the store.
  const people = useQuery(supportPeopleQuery(q.trim(), range, scope));
  const rows = useMemo<PersonRow[]>(
    () =>
      (people.data?.people ?? []).map((p) => ({
        id: p.id,
        email: p.email,
        displayName: p.displayName ?? null,
        projectCount: p.projectCount,
        lastActiveAt: p.lastActiveAt ?? null,
        // The CS payload has no join date; the column renders "-" for it.
        createdAt: null,
        blocked: p.blocked,
        isGuest: p.isGuest,
        // Nor the model lane; null keeps the tag off and the column hidden.
        modelTier: null,
        originCountry: null,
      })),
    [people.data],
  );

  if (people.isPending) return <TableSkeleton columns={6} />;
  if (people.isError) {
    return (
      <AreaBoundary
        area="customer_support"
        error={people.error}
        onRetry={() => void people.refetch()}
      />
    );
  }
  // The TEXT search already ran server-side, so `q` is blanked here to avoid a
  // no-op second pass. The status filter still applies: the CS payload carries
  // `blocked` and the route has no parameter for it.
  return (
    <PeopleTable
      rows={rows}
      q=""
      // Already applied server-side, and the CS payload carries no join date to
      // re-check it against, so the table is handed an empty range rather than
      // a second pass that would drop every row.
      range={EMPTY_DATE_RANGE}
      onRangeChange={onRangeChange}
      statusFilter={statusFilter}
      onStatusChange={onStatusChange}
      scope={scope}
      onScopeChange={onScopeChange}
    />
  );
}

function FullProjects({
  q,
  stageFilter,
  onStageChange,
  range,
  onRangeChange,
  scope,
  onScopeChange,
}: {
  q: string;
  stageFilter?: readonly string[];
  onStageChange?: (next: readonly string[]) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  /** The A/B group this list is narrowed to, applied SERVER-side. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
}) {
  // Keyset-paged (200 a page). Every filter and every sort the server can do
  // travels with the query — see `ServerList` for why that is correctness rather
  // than preference on a paged list.
  const [kinds, setKinds] = useRestoredState<readonly string[]>("explorer/projects/kind", []);
  const [shapes, setShapes] = useRestoredState<readonly string[]>("explorer/projects/shape", []);
  const [buyerTypes, setBuyerTypes] = useRestoredState<readonly string[]>(
    "explorer/projects/buyerType",
    [],
  );
  const [handling, setHandling] = useRestoredState<readonly string[]>(
    "explorer/projects/handling",
    [],
  );
  const [alerts, setAlerts] = useRestoredState<readonly string[]>("explorer/projects/alerts", []);
  const [devices, setDevices] = useRestoredState<readonly string[]>(
    "explorer/projects/devices",
    [],
  );
  const [tiers, setTiers] = useRestoredState<readonly string[]>("explorer/projects/tiers", []);
  const [countries, setCountries] = useRestoredState<readonly string[]>(
    "explorer/projects/countries",
    [],
  );
  const countryOptions = useCountryFilterOptions();
  const [meetings, setMeetings] = useRestoredState<readonly string[]>(
    "explorer/projects/meetings",
    [],
  );
  const [starred, setStarred] = useRestoredState<readonly string[]>(
    "explorer/projects/starred",
    [],
  );
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const search = useDebounced(q.trim(), 300);
  const projects = useInfiniteQuery(
    explorerProjectsQuery({
      q: search,
      range,
      scope,
      stages: stageFilter,
      kinds,
      shapes,
      // The shared control's no-value sentinel is the route's `unset` bucket: ticking
      // "Not classified" must send a value the route accepts, or it is a 422.
      buyerTypes: noValueAs(buyerTypes, "unset"),
      handling,
      alerts: alertSelectionToWire(alerts),
      // The shared control's no-value sentinel is the route's `unknown` bucket
      // (`noValueAs`): ticking Unknown used to send the sentinel itself, a 422.
      devices: noValueAs(devices, "unknown"),
      tiers: noValueAs(tiers, "unknown"),
      countries: noValueAs(countries, "unknown"),
      meetings: noValueAs(meetings, "none"),
      starred: starred.includes("starred"),
      sort: sort ? PROJECT_SERVER_SORTS[sort.key] : undefined,
      desc: sort ? sort.direction === "desc" : undefined,
    }),
  );
  const rows = useMemo<ProjectRow[]>(
    () =>
      (projects.data?.pages ?? []).flatMap((page) =>
        page.projects.map((p: ExplorerProjectRow) => ({
          id: p.id,
          name: p.name,
          summary: p.summary,
          kind: p.kind,
          stage: p.stage,
          workShape: p.workShape ?? null,
          buyerType: p.buyerType ?? null,
          // Null when the payload carries none of the three (a backend older than
          // 0183) — the table then hides the column instead of printing a confident
          // "Unknown" for a field nobody sent.
          device:
            p.createdDevice === undefined &&
            p.createdOs === undefined &&
            p.createdBrowser === undefined
              ? null
              : {
                  class: p.createdDevice ?? null,
                  os: p.createdOs ?? null,
                  browser: p.createdBrowser ?? null,
                },
          modelTier: p.modelTier ?? null,
          originCountry: p.originCountry ?? null,
          budget: p.budget,
          timeline: {
            deliveryMaxDays: p.deliveryMaxDays,
            deliveryMinDays: p.deliveryMinDays ?? null,
            startDate: p.startDate ?? null,
            durationKind: p.durationKind ?? null,
            durationMonths: p.durationMonths ?? null,
            durationMonthsMin: p.durationMonthsMin ?? null,
          },
          ownerLabel: p.owner?.displayName || p.owner?.email || null,
          messageCount: p.counts.messages,
          updatedAt: p.updatedAt,
          alertCount: p.openFlagCount,
          alertReasons: p.openFlagReasons,
          sellerIntent: p.sellerIntent ?? false,
          handling: p,
          starred: p.starred ?? false,
          handoffTrack: p.handoffTrack ?? null,
          meetingVerification: p.meetingVerification ?? null,
        })),
      ),
    [projects.data],
  );
  // Sent with the first page only, so it is read from there rather than from
  // whichever page happens to be last.
  const facets = projects.data?.pages[0]?.facets ?? null;

  if (projects.isPending) return <TableSkeleton columns={5} />;
  if (projects.isError) {
    return (
      <AreaBoundary
        area="explorer"
        error={projects.error}
        onRetry={() => void projects.refetch()}
      />
    );
  }
  return (
    <ProjectsTable
      scope={scope}
      onScopeChange={onScopeChange}
      rows={rows}
      // Applied by the server already; the controls still render and still write
      // the URL. Same split the CS lists use.
      q=""
      range={EMPTY_DATE_RANGE}
      onRangeChange={onRangeChange}
      stageFilter={stageFilter}
      onStageChange={onStageChange}
      kindFilter={kinds}
      onKindChange={setKinds}
      shapeFilter={shapes}
      buyerTypeFilter={buyerTypes}
      onBuyerTypeChange={setBuyerTypes}
      onShapeChange={setShapes}
      handlingFilter={handling}
      onHandlingChange={setHandling}
      alertFilter={alerts}
      onAlertChange={setAlerts}
      deviceFilter={devices}
      onDeviceChange={setDevices}
      tierFilter={tiers}
      onTierChange={setTiers}
      countryFilter={countries}
      onCountryChange={setCountries}
      countryOptions={countryOptions}
      meetingFilter={meetings}
      onMeetingChange={setMeetings}
      starredFilter={starred}
      onStarredChange={setStarred}
      facets={facets}
      server={{
        hasMore: projects.hasNextPage,
        onLoadMore: () => void projects.fetchNextPage(),
        loading: projects.isFetchingNextPage,
        serverSortable: PROJECT_SERVER_SORTABLE,
        onSortChange: setSort,
      }}
    />
  );
}

/** Projects, in support mode. The CS route is cursor-paginated and searches
 *  server-side; only the first page is shown here, and the note says so rather
 *  than letting a partial list read as the whole set. */
function SupportProjects({
  scope,
  onScopeChange,
  q,
  range,
  onRangeChange,
  stageFilter,
  onStageChange,
}: {
  q: string;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  stageFilter?: readonly string[];
  onStageChange?: (next: readonly string[]) => void;
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
}) {
  // Server-side here, like `q`: this route is keyset-paged, so a browser-side
  // date filter could only ever cover the pages already fetched.
  const projects = useInfiniteQuery(supportProjectsQuery(q.trim(), range, scope));
  const rows = useMemo<ProjectRow[]>(
    () =>
      (projects.data?.pages ?? []).flatMap((page) =>
        page.projects.map((p) => ({
          id: p.id,
          name: p.name,
          summary: p.summary ?? null,
          // The CS payload has no `kind`; the column renders the default.
          kind: null,
          // Same ladder as the full listing — both interpolate one SQL fragment.
          stage: p.stage.key,
          // The CS payload is a deliberately narrow allowlist and carries
          // neither. Null hides the columns rather than filling them with "-".
          budget: null,
          timeline: null,
          // The CS payload does not carry the shape; null keeps the tag off
          // and files every row under the filter's no-value bucket.
          workShape: null,
          // Same story as `workShape` above: the CS allowlist does not carry it, so
          // the column hides rather than printing a "-" that reads as classified.
          buyerType: null,
          // Nor the creation device. NULL (not an all-null object) so the column
          // is hidden here rather than reading "Unknown" for every CS row —
          // "we did not send it" and "nothing was recorded" are different facts.
          device: null,
          // Nor the model lane; null hides that column here too.
          modelTier: null,
          originCountry: null,
          ownerLabel: p.owner.displayName || p.owner.email || null,
          messageCount: p.messageCount,
          updatedAt: p.updatedAt ?? null,
          alertCount: p.openFlagCount ?? 0,
          alertReasons: p.openFlagReasons ?? [],
          sellerIntent: p.sellerIntent ?? false,
          // The CS listing carries no handling state and no star. Empty chips
          // and a null star hide both, rather than telling a rep that nobody is
          // on a client a colleague took over an hour ago.
          handling: {},
          starred: null,
          // The CS payload carries no meeting data (talent-adjacent field, same
          // wall as sellerThreads); null hides the column here rather than
          // printing a confident "No meeting" for a fact nobody sent.
          handoffTrack: null,
          meetingVerification: null,
        })),
      ),
    [projects.data],
  );

  if (projects.isPending) return <TableSkeleton columns={5} />;
  if (projects.isError) {
    return (
      <AreaBoundary
        area="customer_support"
        error={projects.error}
        onRetry={() => void projects.refetch()}
      />
    );
  }
  return (
    <>
      {/* Filtered server-side on `q` AND the range, so both local passes are
          skipped; the control still renders and still writes the URL. */}
      <ProjectsTable
        rows={rows}
        q=""
        range={EMPTY_DATE_RANGE}
        onRangeChange={onRangeChange}
        stageFilter={stageFilter}
        onStageChange={onStageChange}
        scope={scope}
        onScopeChange={onScopeChange}
      />
      {projects.hasNextPage ? (
        <div className="ex-load-more">
          <Button
            type="button"
            variant="outline"
            tone="neutral"
            size="sm"
            disabled={projects.isFetchingNextPage}
            onClick={() => void projects.fetchNextPage()}
          >
            {projects.isFetchingNextPage ? "Loading…" : "Load more projects"}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** Tab labels. Named as THINGS ("Projects"), not as scopes ("All projects"):
 *  these are tabs over one list each, and the scope is already implied by the
 *  tab being the whole view. */
const VIEW_LABELS: Record<ExplorerView, string> = {
  people: "People",
  projects: "Projects",
};

/** The tabpanel every tab controls. One shared id rather than one per view, so
 *  each tab points at the panel that is actually on screen. */
const PANEL_ID = "ex-view-panel";

/** Upload a project blueprint (exported from any env) and materialize it as a NEW project
 *  HERE, then jump to it. DEV-ONLY: the button renders only under `import.meta.env.DEV`
 *  and the backend 404s the route outside dev-tier envs — the server is the real gate. */
function ImportProjectButton() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // let the same file be re-picked after a failure
    if (!file) return;
    setBusy(true);
    setFailure(null);
    try {
      const parsed = JSON.parse(await file.text()) as ProjectExport;
      if (!parsed || typeof parsed.name !== "string" || !Array.isArray(parsed.sections)) {
        throw new Error("That file is not a project export.");
      }
      const result = await importProject(parsed);
      await queryClient.invalidateQueries({ queryKey: adminKeys.explorerProjects });
      void navigate({
        to: "/explorer/projects/$projectId",
        params: { projectId: result.project_id },
      });
    } catch (error) {
      // Surfaced, never swallowed (doctrine 1.5): a bad file, or a prod 404.
      setFailure(
        error instanceof ApiError
          ? error.detail
          : error instanceof Error
            ? error.message
            : "Import failed.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ex-import">
      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => void onFile(event)}
      />
      <Button
        variant="outline"
        tone="neutral"
        size="md"
        disabled={busy}
        title={failure ?? "Import an agent-debug bundle exported from any env (dev only)"}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? "Importing…" : "Import debug"}
      </Button>
      {failure ? <span className="ex-import__err">{failure}</span> : null}
    </div>
  );
}

export function ExplorerPage({
  q,
  onQChange,
  view,
  onViewChange,
  stageFilter,
  onStageChange,
  statusFilter,
  onStatusChange,
  range,
  onRangeChange,
  scope,
  onScopeChange,
}: {
  q: string;
  onQChange: (q: string) => void;
  view: ExplorerView;
  onViewChange: (view: ExplorerView) => void;
  /** The A/B group both tables narrow to, held in the URL like the filters
   *  above. Applied SERVER-side by OWNER, so it narrows the whole store rather
   *  than the rows already fetched. */
  scope?: ExperimentScope;
  onScopeChange?: (next: ExperimentScope | undefined) => void;
  /** The date range both tables narrow by, held in the URL like the filters
   *  above it. It means a different column per table — when a person JOINED,
   *  when a project last MOVED — because those are the date each one renders,
   *  and a filter should narrow what the reader is looking at. */
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  /** Stage keys the projects tables keep. Written by the Funnel section's node
   *  clicks and edited by the Stage control above the table. */
  stageFilter?: readonly string[];
  onStageChange?: (next: readonly string[]) => void;
  /** Narrows the people list. `blocked` is the blocked-account roster, and it is
   *  where the launch dashboard's Blocked users tile sends an operator who wants
   *  the durable list rather than the drilldown's snapshot. */
  statusFilter?: readonly string[];
  onStatusChange?: (next: readonly string[]) => void;
}) {
  const mode = useExplorerMode();
  const showPeople = view === "people";
  const showProjects = view === "projects";

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Explorer</h1>
        <p className="ad-page__sub">
          Search people and projects, then open one to see everything about it.
        </p>
      </header>

      <div className="ex-controls">
        <div className="ex-search">
          <Input
            type="search"
            inputSize="md"
            placeholder="Search by name, email, project, or id…"
            aria-label="Search people and projects"
            value={q}
            onChange={(event) => onQChange(event.target.value)}
          />
        </div>
        {/* Import is a dev-only testing tool (lift a real brief into dev to re-run an
            agent against a code change). Hidden outside `vite dev`, and from a viewer
            inside it — it writes a project. The backend route is the real gate for the
            env half (404 in preprod/prod). */}
        {import.meta.env.DEV && canAct(mode) ? <ImportProjectButton /> : null}
      </div>

      {/* Which list you are looking at. TABS rather than the pill group this
          replaced, and above the table rather than beside the search box: as a
          pill next to a search field it read as a filter, so "switch to the
          projects table" looked absent even though `?view=projects` has always
          been a destination. Same tab idiom as the project detail's, so the two
          screens switch the same way.

          Still held in the URL, so "all projects, filtered to X" stays a link
          you can send someone. */}
      <nav className="ex-tabs" role="tablist" aria-label="Explorer sections">
        {EXPLORER_VIEWS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            aria-controls={PANEL_ID}
            className={`ex-tab${view === key ? " ex-tab--active" : ""}`}
            onClick={() => onViewChange(key)}
          >
            {VIEW_LABELS[key]}
          </button>
        ))}
      </nav>

      <div id={PANEL_ID} role="tabpanel">
        {showPeople ? (
          <section className="ad-section">
            <h2 className="ad-section__title">People</h2>
            {mode === "support" ? (
              <SupportPeople
                q={q}
                range={range}
                onRangeChange={onRangeChange}
                statusFilter={statusFilter}
                onStatusChange={onStatusChange}
                scope={scope}
                onScopeChange={onScopeChange}
              />
            ) : (
              <FullPeople
                q={q}
                range={range}
                onRangeChange={onRangeChange}
                statusFilter={statusFilter}
                onStatusChange={onStatusChange}
                scope={scope}
                onScopeChange={onScopeChange}
              />
            )}
          </section>
        ) : null}

        {showProjects ? (
          <section className="ad-section">
            <h2 className="ad-section__title">Projects</h2>
            {mode === "support" ? (
              <SupportProjects
                q={q}
                range={range}
                onRangeChange={onRangeChange}
                stageFilter={stageFilter}
                onStageChange={onStageChange}
                scope={scope}
                onScopeChange={onScopeChange}
              />
            ) : (
              <FullProjects
                q={q}
                range={range}
                onRangeChange={onRangeChange}
                stageFilter={stageFilter}
                onStageChange={onStageChange}
                scope={scope}
                onScopeChange={onScopeChange}
              />
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
